import { Eye, EyeOff } from "lucide-react"
import * as React from "react"
import { useCallback, useRef, useState } from "react"
import { cn } from "src/common/utils"
import { Input, type InputProps } from "src/components/ui/input"

export const REDACTED = "[redacted]"

interface SecretInputProps extends Omit<InputProps, "type"> {
  /** When set and the field has a pre-filled value, shows click-to-clear UX with "Enter new {fieldLabel}" placeholder */
  fieldLabel?: string
}

const SecretInput = React.memo(
  React.forwardRef<HTMLInputElement, SecretInputProps>(
    ({ className, value, onChange, onClick, placeholder, fieldLabel, ...props }, ref) => {
      const preFilled = useRef(!!value).current
      const [visible, setVisible] = useState(false)
      const [dirty, setDirty] = useState(!preFilled)

      const handleClick = useCallback<React.MouseEventHandler<HTMLInputElement>>(
        (e) => {
          if (!dirty) {
            setDirty(true)
            onChange?.({ target: { value: "" }, currentTarget: { value: "" } } as React.ChangeEvent<HTMLInputElement>)
          }
          onClick?.(e)
        },
        [dirty, onChange, onClick],
      )

      const handleChange = useCallback<React.ChangeEventHandler<HTMLInputElement>>(
        (e) => {
          if (!dirty) setDirty(true)
          onChange?.(e)
        },
        [dirty, onChange],
      )

      const toggleVisible = useCallback(() => setVisible((v) => !v), [])

      const resolvedPlaceholder =
        fieldLabel && preFilled && !dirty ? `Leave blank to keep current ${fieldLabel}` : placeholder

      return (
        <span className="relative block">
          <Input
            type={visible && dirty ? "text" : "password"}
            autoComplete="off"
            className={cn(dirty && value && value !== REDACTED && "pr-10", className)}
            value={dirty ? value : ""}
            onChange={handleChange}
            onClick={handleClick}
            placeholder={resolvedPlaceholder}
            ref={ref}
            {...props}
          />
          {dirty && value && value !== REDACTED && (
            <button
              type="button"
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground transition-colors"
              onClick={toggleVisible}
              tabIndex={-1}
            >
              {visible ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
            </button>
          )}
        </span>
      )
    },
  ),
)
SecretInput.displayName = "SecretInput"

export { SecretInput }
